import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Text,
  Stack,
  Button,
  Flex,
  Divider,
  Alert,
  AlertIcon,
  Skeleton,
  SkeletonText,
  Badge,
  Heading,
  Image,
  Code,
  useToast,
  IconButton,
  Tooltip,
  Tag,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
} from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FaCopy, FaPlus, FaTools, FaGraduationCap, FaChartBar } from 'react-icons/fa';
import { InfoIcon } from '@chakra-ui/icons';
import Evaluate from './VisualizationEvaluate';
import Explain from './VisualizationExplain';
import Refine from './VisualizationRefine';
import Edit from './VisualizationEdit';
import { react_api } from '../../../../config';

const VisualizationView = ({ selectedGoal, currentSummary, colors, username, bearerToken }) => {
  // Local state for this component
  const [isLoading, setIsLoading] = useState(false);
  const [visualization, setVisualization] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const toast = useToast();

  // Theme colors
  const themeColors = colors || {
    navbarBg: '#0b4e5aff',
    fontColor: '#0b4e5aff',
    borderColor: '#178da1ff',
    buttonColor: '#083c46ff',
    iconColor: '#9acd32',  
    titleIconColor: '#9acd32',
    servicesBg: '#f6f3ec'
  };

  // Genera la visualizzazione quando viene selezionato un goal o cambiano i dati necessari
  useEffect(() => {
    if (selectedGoal && currentSummary && username && bearerToken) {
      generateVisualization();
    }
  }, [selectedGoal, currentSummary, username, bearerToken]);
  
  // Non effettuiamo più la valutazione automaticamente, ma solo su richiesta dell'utente
  
  // Funzione per aggiornare la visualizzazione (usata da Refine)
  const updateVisualization = (newVisualization) => {
    setVisualization(newVisualization);
  };

  // Funzione per generare la visualizzazione
  const generateVisualization = async () => {
    if (!selectedGoal || !currentSummary) return;
    
    setIsLoading(true);
    setVisualization(null);
    setError(null);
    
    try {
      // Effettua la chiamata API per generare la visualizzazione
      const response = await axios.post(`${react_api}/externalService/novaServices`, {
        username: username,
        BearerToken: bearerToken,
        service: 2,
        request: {
          summary: currentSummary.summary,
          goal: selectedGoal,
          library: "seaborn"
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      // Controlla il formato della risposta e salva la visualizzazione
      if (response.data && response.data.request && response.data.request.charts) {
        const charts = response.data.request.charts;
        if (charts.length > 0 && charts[0].status) {
          setVisualization(charts);
        } else {
          throw new Error('No valid charts in response');
        }
      } else {
        throw new Error('Invalid response format from visualization API');
      }
      
    } catch (err) {
      console.error('Error generating visualization:', err);
      
      let errorMessage = 'Failed to generate visualization. Please try again.';
      
      // Gestione specifica per errori di validazione (422)
      if (err.response?.status === 422) {
        // Estrai il messaggio dettagliato dall'errore di validazione
        const errorDetail = err.response?.data?.detail;
        if (Array.isArray(errorDetail) && errorDetail.length > 0) {
          // Prendi il primo messaggio di errore dettagliato
          errorMessage = errorDetail[0]?.msg || 'Validation error';
        }
      } else {
        // Gestione generica per altri errori
        errorMessage = err.response?.data?.message || err.message || 'Failed to generate visualization';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Handler per rigenerare la visualizzazione
  const handleRegenerateVisualization = () => {
    generateVisualization();
  };
  
  // Funzione per ricevere i charts generati dal componente Edit
  const handleEditVisualization = (charts) => {
    if (!charts || !Array.isArray(charts) || charts.length === 0) {
      setError('Dati di visualizzazione non validi');
      return;
    }  
    try {      
      // Imposta la visualizzazione con i chart ricevuti
      setVisualization(charts);
      
      // Nota: Non è necessario fare una nuova chiamata API qui perché l'elaborazione 
      // è già stata eseguita nel componente Edit
      
    } catch (err) {
      console.error('Error processing visualization data:', err);
      setError('Errore nell\'elaborazione dei dati di visualizzazione: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Le funzioni relative alla valutazione sono state spostate nel componente Evaluate.jsx

  return (
    <Box mt={4} mb={4}>
      {isLoading ? (
        <Box p={4}>
          <Flex justifyContent="space-between" alignItems="center" mb={4}>
            <Skeleton height="30px" width="60%" />
            <Skeleton height="24px" width="100px" />
          </Flex>
          <Skeleton height="2px" mb={6} />
          
          {/* Skeleton per la visualizzazione grafica */}
          <Box borderWidth="1px" borderRadius="md" p={6} borderColor={themeColors.borderColor} bg="white">
            <Flex direction="column" align="center" justify="center">
              <Skeleton height="350px" width="100%" mb={4} borderRadius="md" />
              <Skeleton height="20px" width="70%" mb={2} />
              <Skeleton height="16px" width="50%" />
            </Flex>
          </Box>
          
          <Stack spacing={4} mt={6}>
            <Skeleton height="24px" width="200px" />
            <SkeletonText mt={2} noOfLines={3} spacing={4} skeletonHeight={3} />
          </Stack>
        </Box>
      ) : error ? (
        <Box p={4}>
          <Alert status="error" borderRadius="md" mb={4}>
            <AlertIcon />
            <Text>{error}</Text>
          </Alert>
          <Button
            onClick={handleRegenerateVisualization}
            bg={themeColors.buttonColor}
            color="white"
            _hover={{ bg: '#0a4956' }}
            size="sm"
          >
            Try Again
          </Button>
        </Box>
      ) : visualization ? (
        <Box p={4}>
          {/* Intestazione con icona, domanda del goal, spiegazione e tipo di visualizzazione */}
          <Box mb={6}>
            <Flex justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Flex align="center">
                <Box color={themeColors.titleIconColor} mr={3} fontSize="2xl">
                  <FaChartBar />
                </Box>
                <Heading as="h3" size="md" color={themeColors.fontColor} lineHeight="1.3">
                  {selectedGoal.question || selectedGoal.title}
                </Heading>
              </Flex>
              <Badge colorScheme="teal" fontSize="0.8em" p={1} borderRadius="md">
                Goal ID: {selectedGoal.index}
              </Badge>
            </Flex>
            
            {/* Rationale come sottotitolo */}
            {selectedGoal.rationale && (
              <Text fontSize="md" color="gray.600" mt={2} mb={3} ml={10}>
                {selectedGoal.rationale}
              </Text>
            )}
            
            {/* Tipo di visualizzazione */}
            {selectedGoal.visualization && (
              <Flex mt={3} ml={10}>
                <Tag size="md" variant="subtle" colorScheme="cyan" borderRadius="full">
                  {selectedGoal.visualization}
                </Tag>
              </Flex>
            )}
          </Box>
          
          <Divider mb={6} borderColor={themeColors.borderColor} />
          
          <Stack spacing={6}>
            {/* Visualizzazione principale */}
            <Box 
              borderWidth="1px" 
              borderRadius="md" 
              p={6} 
              borderColor={themeColors.borderColor} 
              bg="white"
              boxShadow="md"
            >
              <Flex direction="column" align="center">
                {/* Visualizzazione immagine generata e codice Python */}
                {Array.isArray(visualization) && visualization.length > 0 && visualization[0].raster ? (
                  <Box width="100%" mb={4}>
                    <Flex direction={["column", "column", "row"]} align="start" justifyContent="space-between" spacing={4}>
                      {/* Colonna sinistra per l'immagine - dimensione maggiore */}
                      <Box flex="1.3" mb={[4, 4, 0]} mr={[0, 0, 4]} minW={["100%", "100%", "55%"]} display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                        <Box 
                          width="100%"
                          minHeight={["300px", "350px", "380px"]} 
                          display="flex" 
                          alignItems="center" 
                          justifyContent="center"
                          bg="white"
                          p={4}
                          borderRadius="md"
                          borderWidth="1px"
                          borderColor="gray.200"
                        >
                          <Image 
                            src={`data:image/png;base64,${visualization[0].raster}`}
                            alt="Generated visualization"
                            borderRadius="md"
                            maxHeight="100%"
                            maxWidth="100%"
                            objectFit="contain"
                          />
                        </Box>
                        <Text fontSize="sm" color="gray.600" mt={2} textAlign="center">
                          Visualization generated based on insight data using Seaborn library
                        </Text>
                      </Box>
                      
                      {/* Colonna destra per il codice Python - dimensione minore */}
                      {visualization[0].code && (
                        <Box flex="1" width={["100%", "100%", "40%"]} maxWidth={["100%", "100%", "40%"]}>
                          {/* Info text with icon */}
                          <Flex align="center" mb={3}>
                            <InfoIcon color="gray.500" mr={2} />
                            <Text fontSize="sm" color="gray.600">
                              How was this visualization created? See the specifications and code below.
                            </Text>
                          </Flex>
                          
                          <Flex align="center" mb={2}>
                            <Text fontWeight="bold" fontSize="md" color={themeColors.fontColor} mr={2}>
                              <span style={{ color: '#4CAF50' }}>&lt;/&gt;</span> Python Code
                            </Text>
                          </Flex>
                          <Box 
                            position="relative"
                            borderRadius="md" 
                            overflow="hidden"
                            boxShadow="md"
                            height="320px" /* Altezza ridotta */
                            bg="#1e1e1e"
                          >
                            {/* Pulsante di copia posizionato in alto a destra sopra il codice */}
                            <Box position="absolute" top="8px" right="8px" zIndex="1">
                              <Tooltip label="Copy code" placement="top" hasArrow>
                                <IconButton
                                  icon={<FaCopy />}
                                  size="sm"
                                  variant="ghost"
                                  colorScheme="whiteAlpha"
                                  color="white"
                                  _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
                                  aria-label="Copy code"
                                  onClick={() => {
                                    navigator.clipboard.writeText(visualization[0].code)
                                      .then(() => {
                                        toast({
                                          title: "Code copied to clipboard",
                                          status: "success",
                                          duration: 2000,
                                          isClosable: true,
                                          position: "top"
                                        });
                                      })
                                      .catch(err => {
                                        console.error('Failed to copy text: ', err);
                                        toast({
                                          title: "Failed to copy",
                                          status: "error",
                                          duration: 2000,
                                          isClosable: true,
                                          position: "top"
                                        });
                                      });
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            
                            {/* Code content with hidden scrollbars but still scrollable */}
                            <Box 
                              className="hide-scrollbars"
                              height="100%" 
                              overflowY="auto"
                              sx={{
                                // Nascondi tutte le barre di scorrimento
                                '&::-webkit-scrollbar': { display: 'none' },
                                '&::-webkit-scrollbar-thumb': { display: 'none' },
                                '&::-webkit-scrollbar-track': { display: 'none' },
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                // Assicurati che anche i figli ereditino queste proprietà
                                '& *::-webkit-scrollbar': { display: 'none' },
                                '& *::-webkit-scrollbar-thumb': { display: 'none' },
                                '& *::-webkit-scrollbar-track': { display: 'none' },
                                '& *': {
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none',
                                }
                              }}
                            >
                              <style jsx global>{`
                                .hide-scrollbars pre {
                                  scrollbar-width: none !important;
                                  -ms-overflow-style: none !important;
                                }
                                .hide-scrollbars pre::-webkit-scrollbar {
                                  display: none !important;
                                  width: 0 !important;
                                  height: 0 !important;
                                }
                                .hide-scrollbars code {
                                  scrollbar-width: none !important;
                                  -ms-overflow-style: none !important;
                                }
                                .hide-scrollbars code::-webkit-scrollbar {
                                  display: none !important;
                                  width: 0 !important;
                                  height: 0 !important;
                                }
                              `}</style>
                              <SyntaxHighlighter
                                language="python"
                                style={atomDark}
                                customStyle={{
                                  margin: 0,
                                  padding: '16px',
                                  paddingTop: '40px',  // Extra padding per il pulsante di copia
                                  height: '100%',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.8rem',
                                  backgroundColor: '#1e1e1e',
                                  scrollbarWidth: 'none',
                                  msOverflowStyle: 'none',
                                  overflow: 'auto'
                                }}
                                showLineNumbers={true}
                                wrapLines={true}
                                wrapLongLines={true}
                              >
                                {visualization[0].code}
                              </SyntaxHighlighter>
                            </Box>
                          </Box>
                        </Box>
                      )}
                    </Flex>
                  </Box>
                ) : (
                  <Box 
                    p={8} 
                    bg="gray.50" 
                    borderRadius="md" 
                    width="100%" 
                    height="350px"
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mb={4}
                    boxShadow="inner"
                  >
                    <Text color={themeColors.fontColor} fontSize="lg" fontWeight="bold" mb={4}>
                      Visualization Output
                    </Text>
                    <Box 
                      p={4} 
                      bg="white" 
                      borderRadius="md" 
                      borderWidth="1px" 
                      borderColor={themeColors.borderColor}
                      width="100%"
                      maxHeight="250px"
                      overflowY="auto"
                      fontFamily="mono"
                      fontSize="sm"
                    >
                      <Text whiteSpace="pre-wrap">
                        {typeof visualization === 'object' 
                          ? JSON.stringify(visualization, null, 2) 
                          : visualization}
                      </Text>
                    </Box>
                  </Box>
                )}
              </Flex>
            </Box>
            
            {/* Section for creating new visualizations from the dataset */}
            <Box mt={8} >
              <Divider mb={4} borderColor={themeColors.borderColor} />
              <Heading as="h4" size="sm" mb={4} color={themeColors.fontColor}>
                <Flex align="center">
                  <Icon as={FaPlus} mr={2} color={themeColors.titleIconColor} />
                  Create a new visualization from the dataset
                </Flex>
              </Heading>
              <Edit
                currentSummary={currentSummary}
                visualization={visualization}
                onGenerateVisualization={handleEditVisualization}
                colors={themeColors}
                username={username}
                bearerToken={bearerToken}
              />
            </Box>
            
            {/* Section for actions on the current visualization */}
            <Box mb={2}>
              <Divider mb={4} borderColor={themeColors.borderColor} />
              <Heading as="h4" size="sm" color={themeColors.fontColor}>
                <Flex align="center">
                  <Icon as={FaTools} mr={2} color={themeColors.titleIconColor} />
                  Actions on the current visualization
                </Flex>
              </Heading>
            </Box>
            
            {/* Tabs for Refine, Explain, Evaluate */}
            <Box>
              <Tabs variant="enclosed" colorScheme="teal" defaultIndex={0} index={activeTab} onChange={(index) => setActiveTab(index)}>
                <TabList>
                  <Tab _selected={{ color: themeColors.fontColor, borderColor: themeColors.borderColor, borderBottomColor: "transparent" }}>
                    <Flex align="center">
                      <Box as="span" mr={2}>
                        {/* Icona per Refine */}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3 17V21H7L17.59 10.41L13.17 6L3 16.17V17ZM21.41 5.83L18.17 2.58L15.16 5.59L18.59 9L21.41 6.17C22.2 5.39 22.2 4.61 21.41 3.83V5.83Z" fill="currentColor"/>
                        </svg>
                      </Box>
                      Refine
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: themeColors.fontColor, borderColor: themeColors.borderColor, borderBottomColor: "transparent" }}>
                    <Flex align="center">
                      <Box as="span" mr={2}>
                        {/* Icona per Explain */}
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM13 14H11V12H13V14ZM13 9C13 9.55 12.55 10 12 10C11.45 10 11 9.55 11 9V7C11 6.45 11.45 6 12 6C12.55 6 13 6.45 13 7V9Z" fill="currentColor"/>
                        </svg>
                      </Box>
                      Explain
                    </Flex>
                  </Tab>
                  <Tab _selected={{ color: themeColors.fontColor, borderColor: themeColors.borderColor, borderBottomColor: "transparent" }}>
                    <Flex align="center">
                      <Box as="span" mr={2}>
                        <FaGraduationCap />
                      </Box>
                      Evaluate
                    </Flex>
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel>
                    {/* Refine Panel - Componente per modificare il grafico con comandi in linguaggio naturale */}
                    <Refine 
                      visualization={visualization} 
                      selectedGoal={selectedGoal} 
                      currentSummary={currentSummary}
                      username={username} 
                      bearerToken={bearerToken}
                      themeColors={themeColors}
                      onVisualizationUpdate={updateVisualization}
                    />
                  </TabPanel>
                  <TabPanel>
                    {/* Explain Panel */}
                    <Explain 
                      visualization={visualization} 
                      selectedGoal={selectedGoal} 
                      username={username} 
                      bearerToken={bearerToken}
                      themeColors={themeColors}
                    />
                  </TabPanel>
                  <TabPanel>
                    {/* Evaluate Panel */}
                    <Evaluate 
                      visualization={visualization} 
                      selectedGoal={selectedGoal} 
                      username={username} 
                      bearerToken={bearerToken}
                      themeColors={themeColors}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          </Stack>
        </Box>
      ) : (
        <Box p={4}>
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Text>No visualization available. Please select an insight to generate a visualization.</Text>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default VisualizationView;
